import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Clock,
  CheckCircle,
  MessageCircle,
  Bell,
  Mail,
  Home,
  Sparkles,
} from "lucide-react";

const TIMELINE_STEPS = [
  {
    icon: CheckCircle,
    title: "Solicitud recibida",
    description: "Tu registro ha sido enviado",
    status: "completed",
  },
  {
    icon: Clock,
    title: "En revisión",
    description: "Casa Matriz está revisando tu solicitud",
    status: "current",
  },
  {
    icon: Bell,
    title: "Notificación",
    description: "Te avisamos por WhatsApp cuando seas aprobado",
    status: "pending",
  },
  {
    icon: Sparkles,
    title: "¡A vender!",
    description: "Accede a tu dashboard y comienza",
    status: "pending",
  },
];

export default function RegistroEspera() {
  const navigate = useNavigate();
  const [nombre, setNombre] = useState("");

  useEffect(() => {
    const storedNombre = sessionStorage.getItem('registro_nombre');
    if (storedNombre) {
      setNombre(storedNombre);
      sessionStorage.removeItem('registro_nombre');
    }
  }, []);

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
      {/* Success Header */}
      <div className="text-center space-y-4">
        {/* Animated Check */}
        <div className="relative w-24 h-24 mx-auto">
          <div className="absolute inset-0 bg-green-100 rounded-full animate-ping opacity-20" />
          <div className="relative w-24 h-24 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center shadow-lg shadow-green-500/30">
            <CheckCircle className="w-12 h-12 text-white" />
          </div>
        </div>

        <div className="space-y-2">
          <h1 className="font-['Playfair_Display'] text-2xl sm:text-3xl text-[#4A2C0A] font-bold">
            {nombre ? `¡Gracias, ${nombre}!` : "¡Solicitud enviada!"}
          </h1>
          <p className="font-['Poppins'] text-[#4A2C0A]/70 max-w-sm mx-auto">
            Tu registro está siendo revisado por nuestro equipo.
            Te contactaremos pronto.
          </p>
        </div>
      </div>

      {/* Timeline */}
      <div className="bg-white rounded-2xl p-6 shadow-sm">
        <h3 className="font-['Playfair_Display'] text-lg font-semibold text-[#4A2C0A] mb-4">
          ¿Qué sigue?
        </h3>
        
        <div className="relative">
          {/* Vertical line */}
          <div className="absolute left-5 top-8 bottom-8 w-0.5 bg-[#E5D4B3]" />
          
          <div className="space-y-6">
            {TIMELINE_STEPS.map((step, index) => {
              const isCompleted = step.status === "completed";
              const isCurrent = step.status === "current";
              const Icon = step.icon;
              
              return (
                <div key={index} className="flex gap-4 relative">
                  {/* Icon */}
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 z-10 ${
                      isCompleted
                        ? "bg-green-500 text-white"
                        : isCurrent
                        ? "bg-[#C8902A] text-white ring-4 ring-[#C8902A]/20"
                        : "bg-[#E5D4B3] text-[#4A2C0A]/40"
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                  </div>
                  
                  {/* Content */}
                  <div className={`flex-1 pt-1 ${!isCurrent && !isCompleted ? "opacity-50" : ""}`}>
                    <p className={`font-['Poppins'] font-medium ${
                      isCurrent ? "text-[#C8902A]" : "text-[#4A2C0A]"
                    }`}>
                      {step.title}
                    </p>
                    <p className="text-sm text-[#4A2C0A]/60 font-['Poppins']">
                      {step.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Notification Methods */}
      <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-5 space-y-3">
        <h3 className="font-['Poppins'] font-medium text-green-800 flex items-center gap-2">
          <MessageCircle className="w-5 h-5" />
          Te notificaremos por:
        </h3>
        <div className="flex flex-wrap gap-3">
          <div className="inline-flex items-center gap-2 bg-white px-3 py-2 rounded-lg text-sm font-['Poppins']">
            <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
              <MessageCircle className="w-3.5 h-3.5 text-white" />
            </div>
            <span className="text-green-800">WhatsApp</span>
          </div>
          <div className="inline-flex items-center gap-2 bg-white px-3 py-2 rounded-lg text-sm font-['Poppins']">
            <div className="w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center">
              <Bell className="w-3.5 h-3.5 text-white" />
            </div>
            <span className="text-amber-800">Notificación in-app</span>
          </div>
          <div className="inline-flex items-center gap-2 bg-white px-3 py-2 rounded-lg text-sm font-['Poppins']">
            <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
              <Mail className="w-3.5 h-3.5 text-white" />
            </div>
            <span className="text-blue-800">Email</span>
          </div>
        </div>
      </div>

      {/* Estimated Time */}
      <div className="bg-[#FEF3E2] rounded-2xl p-5 flex items-center gap-4">
        <div className="w-12 h-12 bg-[#C8902A]/20 rounded-xl flex items-center justify-center">
          <Clock className="w-6 h-6 text-[#C8902A]" />
        </div>
        <div>
          <p className="font-['Poppins'] font-medium text-[#4A2C0A]">
            Tiempo estimado de aprobación
          </p>
          <p className="text-sm text-[#C8902A] font-['Poppins'] font-semibold">
            24 a 48 horas hábiles
          </p>
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-col gap-3 pb-16">
        <Link to="/">
          <Button
            className="w-full h-12 bg-[#C8902A] hover:bg-[#A67720] text-white rounded-xl font-['Poppins']"
            data-testid="btn-volver-inicio"
          >
            <Home className="w-4 h-4 mr-2" />
            Volver al inicio
          </Button>
        </Link>
        <Link to="/login">
          <Button
            variant="outline"
            className="w-full h-12 border-[#E5D4B3] text-[#4A2C0A] rounded-xl font-['Poppins']"
          >
            ¿Ya tienes cuenta? Inicia sesión
          </Button>
        </Link>
      </div>
    </div>
  );
}
